import React, { useState } from 'react';
import { Zap, Brain, BarChart3, Users } from 'lucide-react';
interface Feature {
  id: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  badge?: string;
}
export function FeatureSectionModern() {
  const [activeFeature, setActiveFeature] = useState(0);
  const features: Feature[] = [{
    id: 0,
    icon: <Zap className="w-6 h-6" data-id="element-354" />,
    title: 'Automated MLS Offers',
    description: 'Send 1,000+ offers per week with zero manual work. Our AI handles everything from property selection to offer generation.',
    badge: 'Most Popular'
  }, {
    id: 1,
    icon: <Brain className="w-6 h-6" data-id="element-355" />,
    title: 'Smart Property Analysis',
    description: 'AI evaluates every property and generates optimal offers based on market data, comps, and your investment criteria.'
  }, {
    id: 2,
    icon: <BarChart3 className="w-6 h-6" data-id="element-356" />,
    title: 'Deal Pipeline Tracking',
    description: 'Track every offer from send to close in one unified dashboard. See response rates, follow-ups, and conversions.'
  }, {
    id: 3,
    icon: <Users className="w-6 h-6" data-id="element-357" />,
    title: 'Agent Relationship Management',
    description: 'Build lasting relationships with automated follow-ups, personalized messaging, and engagement tracking.'
  }];
  return <section className="relative w-full overflow-hidden" data-id="element-358">
      {/* Split Background Container */}
      <div className="relative min-h-screen flex" data-id="element-359">
        {/* Left Side - Dark Background */}
        <div className="w-full lg:w-1/2 bg-[#070707] relative" data-id="element-360">
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#070707] to-[#050505]" data-id="element-361" />

          {/* Content */}
          <div className="relative z-10 flex flex-col justify-center min-h-screen px-8 sm:px-12 lg:px-16 xl:px-24 py-20" data-id="element-362">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#3ab795]/10 border border-[#3ab795]/20 rounded-full backdrop-blur-sm mb-8 w-fit" data-id="element-363">
              <div className="w-2 h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-364" />
              <span className="text-sm font-medium text-[#3ab795] tracking-wide uppercase" data-id="element-365">
                How It Works
              </span>
            </div>

            {/* Heading */}
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-366">
              All Your Deals,
              <br data-id="element-367" />
              <span className="text-[#3ab795]" data-id="element-368">Organized Effortlessly</span>
            </h2>

            <p className="text-lg sm:text-xl text-gray-400 mb-12 max-w-xl leading-relaxed" data-id="element-369">
              AcquireFlow automates your entire MLS offer workflow, from
              property analysis to agent follow-ups.
            </p>

            {/* Feature Cards */}
            <div className="space-y-4" data-id="element-370">
              {features.map(feature => <button key={feature.id} onClick={() => setActiveFeature(feature.id)} className={`w-full text-left p-6 rounded-2xl transition-all duration-300 group ${activeFeature === feature.id ? 'bg-[#1a1a1a] border-2 border-[#3ab795]' : 'bg-[#0f0f0f] border-2 border-white/5 hover:border-white/10'}`} data-id="element-371">
                  <div className="flex items-start gap-4" data-id="element-372">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${activeFeature === feature.id ? 'bg-[#3ab795] text-white' : 'bg-white/5 text-[#3ab795] group-hover:bg-white/10'}`} data-id="element-373">
                      {feature.icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0" data-id="element-374">
                      <div className="flex items-center gap-3 mb-2" data-id="element-375">
                        <h3 className={`text-lg font-semibold transition-colors duration-300 ${activeFeature === feature.id ? 'text-white' : 'text-gray-300 group-hover:text-white'}`} data-id="element-376">
                          {feature.title}
                        </h3>
                        {feature.badge && activeFeature === feature.id && <span className="px-2 py-1 text-xs font-medium bg-[#3ab795]/20 text-[#3ab795] rounded-full" data-id="element-377">
                            {feature.badge}
                          </span>}
                      </div>
                      <p className={`text-sm leading-relaxed transition-all duration-300 ${activeFeature === feature.id ? 'text-gray-400 opacity-100 max-h-20' : 'text-gray-500 opacity-0 max-h-0 overflow-hidden'}`} data-id="element-378">
                        {feature.description}
                      </p>
                    </div>

                    {/* Active Indicator */}
                    {activeFeature === feature.id && <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-379" />}
                  </div>
                </button>)}
            </div>

            {/* CTA */}
            <div className="mt-12" data-id="element-380">
              <a href="/trial" className="inline-flex items-center gap-3 px-8 py-4 bg-[#3ab795] text-white font-semibold text-lg rounded-xl transition-all duration-300 hover:bg-[#2d9f7e] hover:scale-105 hover:shadow-[0_8px_24px_rgba(58,183,149,0.3)] group" data-id="element-381">
                <span data-id="element-382">Start Free Trial</span>
                <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-383">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" data-id="element-384" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Right Side - Light Background with Product Screenshot */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-gray-50 to-gray-100 relative items-center justify-center p-12 xl:p-16" data-id="element-385">
          {/* Decorative Elements */}
          <div className="absolute top-20 right-20 w-64 h-64 bg-[#3ab795]/5 rounded-full blur-3xl" data-id="element-386" />
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-[#3ab795]/5 rounded-full blur-3xl" data-id="element-387" />

          {/* Product Screenshot Container */}
          <div className="relative z-10 w-full max-w-2xl" data-id="element-388">
            {/* Main Dashboard Card */}
            <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.15)] overflow-hidden border border-gray-200" data-id="element-389">
              {/* Dashboard Header */}
              <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 px-6 py-4" data-id="element-390">
                <div className="flex items-center justify-between" data-id="element-391">
                  <div className="flex items-center gap-3" data-id="element-392">
                    <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-393" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-394" />
                    <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-395" />
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-[#3ab795]/10 rounded-lg" data-id="element-396">
                    <div className="w-2 h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-397" />
                    <span className="text-sm font-medium text-[#3ab795]" data-id="element-398">
                      Live
                    </span>
                  </div>
                </div>
              </div>

              {/* Dashboard Content */}
              <div className="p-6 space-y-6" data-id="element-399">
                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-4" data-id="element-400">
                  <div className="bg-gradient-to-br from-[#3ab795]/10 to-[#3ab795]/5 rounded-xl p-4 border border-[#3ab795]/20" data-id="element-401">
                    <div className="text-2xl font-bold text-gray-900" data-id="element-402">
                      1,247
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-403">Offers Sent</div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-4 border border-blue-200" data-id="element-404">
                    <div className="text-2xl font-bold text-gray-900" data-id="element-405">89</div>
                    <div className="text-sm text-gray-600" data-id="element-406">Responses</div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl p-4 border border-purple-200" data-id="element-407">
                    <div className="text-2xl font-bold text-gray-900" data-id="element-408">12</div>
                    <div className="text-sm text-gray-600" data-id="element-409">Deals Closed</div>
                  </div>
                </div>

                {/* Active Deals List */}
                <div className="space-y-3" data-id="element-410">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide" data-id="element-411">
                    Active Deals
                  </h3>

                  {[{
                  address: '1234 Oak Street',
                  status: 'Negotiating',
                  color: 'yellow'
                }, {
                  address: '5678 Maple Ave',
                  status: 'Under Contract',
                  color: 'green'
                }, {
                  address: '9012 Pine Road',
                  status: 'Pending',
                  color: 'blue'
                }].map((deal, idx) => <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors" data-id="element-412">
                      <div data-id="element-413">
                        <div className="font-medium text-gray-900" data-id="element-414">
                          {deal.address}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-415">
                          Updated 2 hours ago
                        </div>
                      </div>
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${deal.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' : deal.color === 'green' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-416">
                        {deal.status}
                      </span>
                    </div>)}
                </div>

                {/* Chart Placeholder */}
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200" data-id="element-417">
                  <div className="flex items-center justify-between mb-4" data-id="element-418">
                    <h3 className="text-sm font-semibold text-gray-700" data-id="element-419">
                      Response Rate
                    </h3>
                    <span className="text-sm text-[#3ab795] font-medium" data-id="element-420">
                      +12% this week
                    </span>
                  </div>
                  <div className="h-32 flex items-end gap-2" data-id="element-421">
                    {[40, 65, 45, 80, 60, 90, 75].map((height, idx) => <div key={idx} className="flex-1 bg-gradient-to-t from-[#3ab795] to-[#3ab795]/50 rounded-t-lg transition-all duration-300 hover:from-[#2d9f7e] hover:to-[#2d9f7e]/50" style={{
                    height: `${height}%`
                  }} data-id="element-422" />)}
                  </div>
                </div>
              </div>
            </div>

            {/* Floating UI Element */}
            <div className="absolute -top-6 -right-6 bg-white rounded-xl shadow-[0_12px_32px_rgba(0,0,0,0.12)] p-4 border border-gray-200 animate-float" data-id="element-423">
              <div className="flex items-center gap-3" data-id="element-424">
                <div className="w-10 h-10 rounded-full bg-[#3ab795] flex items-center justify-center" data-id="element-425">
                  <Zap className="w-5 h-5 text-white" data-id="element-426" />
                </div>
                <div data-id="element-427">
                  <div className="text-sm font-semibold text-gray-900" data-id="element-428">
                    New Offer Sent
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-429">Just now</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Product Screenshot */}
      <div className="lg:hidden bg-gradient-to-br from-gray-50 to-gray-100 px-6 py-16" data-id="element-430">
        <div className="max-w-2xl mx-auto" data-id="element-431">
          <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.15)] overflow-hidden border border-gray-200" data-id="element-432">
            {/* Same content as desktop but simplified */}
            <div className="p-6 space-y-4" data-id="element-433">
              <div className="grid grid-cols-3 gap-3" data-id="element-434">
                <div className="bg-[#3ab795]/10 rounded-lg p-3 border border-[#3ab795]/20" data-id="element-435">
                  <div className="text-xl font-bold text-gray-900" data-id="element-436">1,247</div>
                  <div className="text-xs text-gray-600" data-id="element-437">Offers</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200" data-id="element-438">
                  <div className="text-xl font-bold text-gray-900" data-id="element-439">89</div>
                  <div className="text-xs text-gray-600" data-id="element-440">Responses</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 border border-purple-200" data-id="element-441">
                  <div className="text-xl font-bold text-gray-900" data-id="element-442">12</div>
                  <div className="text-xs text-gray-600" data-id="element-443">Closed</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}